import {BaseApi} from './BaseApi'
import {sanitizePayload} from '../utils/apiUtils'
import type {ITimeOffPolicy} from '../models/timeOff'
import {reportCatchError} from '../utils/errorReporter'

// Enums matching MSP NodeJS service
export enum SiteParentType {
  Region = 'Region',
  Company = 'Company',
}

// Interfaces matching MSP NodeJS service schemas
export interface Address {
  line1: string
  city: string
  state: string
  zipCode: string
  display?: string
}

export interface Department {
  id: string
  name: string
  departmentMainContact: string
  departmentMainContactPhone: string
  departmentMainContactEmail: string
  description?: string
}

// Enum for company payroll frequency
export enum CompanyPayRollFrequency {
  Weekly = 'Weekly',
  BiWeekly = 'BiWeekly',
  SemiMonthly = 'SemiMonthly',
  Monthly = 'Monthly',
}

export interface Site {
  id: string
  _id?: string
  name: string
  parentId: string
  parentType: SiteParentType
  departments?: Department[]
  address: Address
  phoneNumber: string
  phoneNumberExt?: number
  paySchedule?: CompanyPayRollFrequency
  standardPayDaylag?: number
  payPeriodStartDay?: Date
  payPeriodEndDay?: Date
  createdDate?: string
  modifiedDate?: string
  timeOffPolicies?: ITimeOffPolicy[]
  allowManualWeeklyHours?: boolean
}

// Site Profile Response with operational information
export interface SiteProfileResponse {
  id: string
  name: string
  parentId: string
  parentType: string
  departments?: Department[]
  address: Address
  phoneNumber: string
  phoneNumberExt: number
  companyName: string
  regionName: string
  eor: {id: string; name: string}
  paySchedule: CompanyPayRollFrequency
  standardPayDaylag: number
  payPeriodStartDay: Date
  payPeriodEndDay: Date
  timeOffPolicies?: ITimeOffPolicy[]
  allowManualWeeklyHours?: boolean
  // RONOS (Staffology) sync status — absent when the site is not linked to RONOS
  ronosSyncStatus?: 'pending' | 'synced' | 'failed'
  ronosSyncError?: string
  /** The site's stored IANA timezone (chosen by an admin or found from its address). */
  timeZone?: string
  /** The zone the site's order times use: timeZone, else the one guessed from its state. */
  effectiveTimeZone?: string
}

// Request DTOs matching MSP NodeJS service
export interface CreateSiteRequest {
  name: string
  parentId: string
  parentType: SiteParentType
  departments?: Department[]
  address: Address
  phoneNumber: string
  phoneNumberExt?: number
  allowManualWeeklyHours?: boolean
  /** IANA timezone; leave it out to have the server find it from the address. */
  timeZone?: string
}

export interface AddDepartmentToSiteRequest {
  siteId: string
  name: string
  departmentMainContact: string
  departmentMainContactPhone: string
  departmentMainContactEmail: string
  description?: string
}

export interface UpdateDepartmentRequest {
  name?: string
  departmentMainContact?: string
  departmentMainContactPhone?: string
  departmentMainContactEmail?: string
  description?: string
}

// Update Site Profile Request DTO (matching new backend endpoint)
export interface UpdateSiteProfileRequest {
  name?: string
  address?: Address
  phoneNumber?: string
  phoneNumberExt?: number
  paySchedule?: CompanyPayRollFrequency
  standardPayDaylag?: number
  payPeriodStartDay?: Date
  payPeriodEndDay?: Date
  allowManualWeeklyHours?: boolean
  /**
   * IANA timezone. Leave it out to keep the stored one; null to have the server find it from the
   * address again ("" would not arrive: sanitizePayload drops empty strings).
   */
  timeZone?: string | null
}

/**
 * Site API service for MSP NodeJS endpoints
 * Handles site creation, management, and department operations
 */
export class SiteApi extends BaseApi {
  /**
   * Get site by ID
   * GET /api/site/getSite/:id
   */
  async getSiteById(id: string): Promise<Site | null> {
    try {
      const response = await this.client.get<Site | null>(`api/site/getSite/${id}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching site:', error)
      throw error
    }
  }

  /**
   * Get site profile with operational information
   * GET /api/site/getSiteProfile/:id
   */
  async getSiteProfile(id: string): Promise<SiteProfileResponse> {
    try {
      const response = await this.client.get<SiteProfileResponse>(`api/site/getSiteProfile/${id}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching site profile:', error)
      throw error
    }
  }

  /**
   * Update site profile with operational information
   * PUT /api/site/updateSiteProfile/:id
   */
  async updateSiteProfile(
    id: string,
    updateData: UpdateSiteProfileRequest,
  ): Promise<SiteProfileResponse> {
    try {
      const sanitizedData = sanitizePayload(updateData)
      const response = await this.client.put<SiteProfileResponse>(
        `api/site/updateSiteProfile/${id}`,
        sanitizedData,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating site profile:', error)
      throw error
    }
  }

  /**
   * Get all sites for a company
   * GET /api/site/getSitesByCompanyId/:companyId
   */
  async getSitesByCompanyId(companyId: string): Promise<Site[]> {
    try {
      const response = await this.client.get<Site[]>(`api/site/getSitesByCompanyId/${companyId}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching sites for company:', error)
      throw error
    }
  }

  /**
   * Create a new site
   * POST /api/site/onboardSite
   */
  async createSite(siteData: CreateSiteRequest): Promise<Site> {
    try {
      const sanitizedData = sanitizePayload(siteData)
      const response = await this.client.post<Site>('api/site/onboardSite', sanitizedData)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error creating site:', error)
      throw error
    }
  }

  /**
   * Add department to site
   * PUT /api/site/addDepartment
   */
  async addDepartment(departmentData: AddDepartmentToSiteRequest): Promise<Site> {
    try {
      const sanitizedData = sanitizePayload(departmentData)
      const response = await this.client.put<Site>('api/site/addDepartment', sanitizedData)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error adding department to site:', error)
      throw error
    }
  }

  /**
   * Update department in site
   * PUT /api/site/updateDepartment
   */
  async updateDepartment(
    siteId: string,
    departmentId: string,
    updateData: UpdateDepartmentRequest,
  ): Promise<Site> {
    try {
      const sanitizedData = sanitizePayload(updateData)
      const response = await this.client.put<Site>(
        `api/site/updateDepartment?siteId=${siteId}&departmentId=${departmentId}`,
        sanitizedData,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating department:', error)
      throw error
    }
  }

  /**
   * Delete department from site
   * DELETE /api/site/deleteDepartment
   */
  async deleteDepartment(siteId: string, departmentId: string): Promise<Site> {
    try {
      const response = await this.client.delete<Site>(
        `api/site/deleteDepartment?siteId=${siteId}&departmentId=${departmentId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error deleting department:', error)
      throw error
    }
  }
}

export const siteApi = new SiteApi()
